// ── Landing Page ──────────────────────────────────────────────
// Public page for unauthenticated users: value prop + sign-in form.
// Primary auth path is email/password (works in dev without allowlist);
// "Continue with Google" is kept as a secondary option.

import { useState } from 'react';
import { useAuth } from './AuthContext';

// Map Firebase auth error codes to user-facing messages
function mapAuthError(err) {
  const code = err?.code || '';
  switch (code) {
    case 'auth/invalid-email':         return 'That email address is not valid.';
    case 'auth/user-not-found':
    case 'auth/invalid-credential':
    case 'auth/wrong-password':        return 'Email or password is incorrect.';
    case 'auth/email-already-in-use':  return 'An account with this email already exists. Try signing in instead.';
    case 'auth/weak-password':         return 'Password must be at least 6 characters.';
    case 'auth/too-many-requests':     return 'Too many attempts. Please wait a minute and try again.';
    case 'auth/operation-not-allowed': return 'Email/password sign-in is not enabled on this Firebase project. Enable it in Firebase Console → Authentication → Sign-in method.';
    case 'auth/network-request-failed':return 'Network error. Check your connection.';
    default:                           return err?.message || 'Sign-in failed. Please try again.';
  }
}

const FEATURES = [
  { icon: '◈', title: 'Idea Exploration', desc: 'Transform a raw idea into a structured thinking tree with AI agents', color: '#6c63ff' },
  { icon: '⚖', title: 'Multi-Agent Debate', desc: 'Challenge your thinking with adversarial critique, rebuttal, and synthesis', color: '#ffa94d' },
  { icon: '⟨/⟩', title: 'Research-Grounded', desc: 'AI agents research the web to ground every node in real data', color: '#20c997' },
  { icon: '✦', title: 'Export & Share', desc: 'Share interactive trees via link, export as PNG, SVG, or HTML', color: '#f06595' },
];

const MODES = [
  { icon: '◈', label: 'IDEA', desc: 'Product & startup thinking', color: '#6c63ff' },
  { icon: '⟨/⟩', label: 'CODE', desc: 'Codebase reverse-engineering', color: '#20c997' },
  { icon: '⚖', label: 'DECIDE', desc: 'Decision frameworks', color: '#ffa94d' },
  { icon: '✦', label: 'WRITE', desc: 'Content planning', color: '#f06595' },
  { icon: '◉', label: 'PLAN', desc: 'Project roadmapping', color: '#69db7c' },
  { icon: '⧫', label: 'LEARN', desc: 'Concept deep-dives', color: '#34d399' },
];

// Production = Google-only sign-in. Email/password available in dev so local
// devs can spin up accounts without going through the Firebase Google-OAuth
// popup (which requires a real domain allowlist in Firebase Console).
const EMAIL_AUTH_ENABLED = process.env.NODE_ENV !== 'production';

export default function LandingPage({ shareId }) {
  const { login, loginWithEmail, signupWithEmail } = useAuth();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'signup') {
        await signupWithEmail(email.trim(), password);
      } else {
        await loginWithEmail(email.trim(), password);
      }
      // On success, AuthProvider's onAuthStateChanged will swap us out of the LandingPage.
    } catch (err) {
      setError(mapAuthError(err));
      setBusy(false);
    }
  };

  return (
    <div className="landing">
      {/* Hero */}
      <header className="landing-hero">
        <div className="landing-logo">◈</div>
        <h1 className="landing-title">THOUGHTCLAW</h1>
        <p className="landing-tagline">
          {shareId
            ? 'Sign in to view this shared thinking tree.'
            : 'AI-powered structured thinking. Turn any idea into a visual thinking tree with research agents, multi-agent debate, and interactive exploration.'
          }
        </p>
        <a href="/blog" className="landing-blog-link">Read the manifesto →</a>

        {/* Email/password form — dev-only. Prod is Google-only. */}
        {EMAIL_AUTH_ENABLED && (
        <form className="landing-auth-form" onSubmit={handleSubmit} noValidate>
          <div className="landing-auth-tabs" role="tablist" aria-label="Authentication mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signin'}
              className={`landing-auth-tab ${mode === 'signin' ? 'active' : ''}`}
              onClick={() => { setMode('signin'); setError(null); }}
            >Sign in</button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'signup'}
              className={`landing-auth-tab ${mode === 'signup' ? 'active' : ''}`}
              onClick={() => { setMode('signup'); setError(null); }}
            >Create account</button>
          </div>

          <label className="landing-auth-label" htmlFor="tc-email">Email</label>
          <input
            id="tc-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="landing-auth-input"
            placeholder="you@example.com"
            disabled={busy}
          />

          <label className="landing-auth-label" htmlFor="tc-password">Password</label>
          <input
            id="tc-password"
            type="password"
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="landing-auth-input"
            placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'}
            disabled={busy}
          />

          {error && <div className="landing-auth-error" role="alert">{error}</div>}

          <button type="submit" className="landing-cta landing-cta-primary" disabled={busy || !email || !password}>
            {busy ? 'Signing in…' : (mode === 'signup' ? 'Create account' : 'Sign in')}
          </button>
        </form>
        )}

        {EMAIL_AUTH_ENABLED && <div className="landing-auth-divider"><span>or</span></div>}

        <button className="landing-cta landing-cta-google" onClick={login} disabled={busy}>
          <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: 8, verticalAlign: 'middle' }}>
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>
      </header>

      {/* Features */}
      <section className="landing-features">
        {FEATURES.map((f, i) => (
          <div key={i} className="landing-feature-card">
            <div className="landing-feature-icon" style={{ color: f.color }}>{f.icon}</div>
            <h3 className="landing-feature-title">{f.title}</h3>
            <p className="landing-feature-desc">{f.desc}</p>
          </div>
        ))}
      </section>

      {/* Modes */}
      <section className="landing-modes">
        <h2 className="landing-section-title">6 THINKING MODES</h2>
        <div className="landing-mode-grid">
          {MODES.map((m, i) => (
            <div key={i} className="landing-mode-card" style={{ borderColor: m.color + '40' }}>
              <span className="landing-mode-icon" style={{ color: m.color }}>{m.icon}</span>
              <span className="landing-mode-label" style={{ color: m.color }}>{m.label}</span>
              <span className="landing-mode-desc">{m.desc}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <span style={{ opacity: 0.4 }}>Built with Claude &middot; ThoughtClaw</span>
      </footer>
    </div>
  );
}
